﻿import { rootElement } from '@shared/setup';
import Root from '@shared/Root/Root.vue';
import { createRouter, createWebHistory } from 'vue-router';
import FormsList from '@views/Web/FormsList/FormsList.vue';
import FormEditor from '@views/Web/FormEditor/FormEditor.vue';
import MediaMonitorCreateNew from '@views/MediaMonitors/MediaMonitorCreateNew.vue';
import MediaMonitorsList from '@views/MediaMonitors/MediaMonitorsList.vue';
import MediaMonitor from '@views/MediaMonitors/MediaMonitor.vue';
import AccountEvaluation from '@views/Account/AccountEvaluation/AccountEvaluation.vue';
import PaymentFailed from '@views/Account/PaymentFailed/PaymentFailed.vue';
import TextMessagesList from '@views/TextMessages/List.vue';
import NewTextMessage from '@views/TextMessages/New.vue';
import TextMessagesKeywords from '@views/TextMessages/Keywords.vue';
import TextMessagesResponses from '@views/TextMessages/Responses.vue';
import TextMessageSending from '@views/TextMessages/Sending.vue';
import SmsDialogs from '@views/TextMessages/SmsDialogs.vue';
import Contact from '@views/Contact/Contact.vue';
import SurveysList from '@views/Surveys/List.vue';
import SurveysNew from '@views/Surveys/New.vue';
import SegmentsRoot from '@views/Segments/SegmentsRoot.vue';
import SurveyPublished from '@views/Surveys/Published.vue';
import ViewsRoot from '@views/ViewsRoot.vue';
import BackOfficeRoot from '@cwd/Views/BackOffice/BackOfficeRoot.vue';
import AccountList from '@cwd/Views/BackOffice/Account/AccountList.vue';
import AccountEdit from '@cwd/Views/BackOffice/Account/AccountEdit.vue';
import SubscriptionList from '@cwd/Views/BackOffice/Subscription/SubscriptionList.vue';
import SubscriptionEdit from '@cwd/Views/BackOffice/Subscription/SubscriptionEdit.vue';
import UserList from '@cwd/Views/BackOffice/User/UserList.vue';
import UserEdit from '@cwd/Views/BackOffice/User/UserEdit.vue';
import Features from '@cwd/Views/BackOffice/Features/Features.vue';
import FeatureSets from '@cwd/Views/BackOffice/FeatureSets/FeatureSets.vue';
import ServiceSubscriptionStats from '@cwd/Views/BackOffice/ServiceSubscriptionStats/ServiceSubscriptionStats.vue';
import ManageUsers from '@cwd/Views/BackOffice/ManageUsers/ManageUsers.vue';
import WorkerManagement from '@cwd/Views/BackOffice/WorkerManagement/WorkerManagement.vue';
import TaskManager from '@cwd/Views/BackOffice/TaskManager/TaskManager.vue';
import Themes from '@cwd/Views/BackOffice/Themes/Themes.vue';
import SegmentsList from '@cwd/Views/Segments/SegmentsList.vue';
import SegmentsEdit from '@cwd/Views/Segments/SegmentsEdit.vue';
import JourneyHome from '@journeys/Pages/JourneyStart.vue';
import JourneyNew from '@journeys/Pages/JourneyNew.vue';
import Viewport from '@journeys/components/Viewport.vue';
import JourneysRoot from '@journeys/JourneysRoot.vue';
import EventList from '@views/Event/EventList.vue';
import EventSponsors from '@views/Event/Sponsors.vue';
import EventSponsor from '@views/Event/Sponsor.vue';
import EventsNew from '@views/Event/New.vue';
import Dashboard from '@cwd/Views/Dashboard/Dashboard.vue';
import SmtpServers from '@views/BackOffice/SmtpServers/SmtpServers.vue';
import SmtpServerDetails from '@views/BackOffice/SmtpServers/SmtpServerDetails.vue';
import OAuthProviderList from '@cwd/Views/BackOffice/OAuthProvider/OAuthProviderList.vue';
import EditOAuthProvider from '@cwd/Views/BackOffice/OAuthProvider/EditOAuthProvider.vue';
import OAuthProviderAuditLogList from '@cwd/Views/BackOffice/OAuthProvider/OAuthProviderAuditLogList.vue';
import LatestIssues from '@cwd/Views/BackOffice/LatestIssues/LatestIssues.vue';
import Speakers from '@views/Event/Speakers.vue';
import Speaker from '@views/Event/Speaker.vue';

const initVueRoot = async () => {
    const routes: any[] = [
        {
            path: '/Account/PaymentFailed',
            name: 'PaymentFailed',
            component: PaymentFailed,
        },
        {
            path: '/Account/Evaluation',
            name: 'AccountEvaluation',
            component: AccountEvaluation,
        },
        {
            path: '/Dashboard',
            component: ViewsRoot,
            children: [{ path: '', name: 'Dashboard', component: Dashboard }],
        },
        {
            path: '/MediaMonitors',
            component: ViewsRoot,
            children: [
                { path: '', name: 'MediaMonitors', component: MediaMonitorsList },
                { path: 'New', name: 'NewMediaMonitor', component: MediaMonitorCreateNew },
                { path: ':id', name: 'MediaMonitor', component: MediaMonitor, props: true },
            ],
        },
        {
            path: '/Web/Forms',
            component: ViewsRoot,
            children: [
                { path: '', name: 'Forms', component: FormsList, props: true },
                { path: 'new', name: 'FormsNew', component: FormEditor, props: true },
                { path: 'copy/:copyFromWebContentId', name: 'FormsCopy', component: FormEditor, props: true },
                {
                    path: 'edit/:webContentId',
                    component: FormEditor,
                    name: 'FormsEditor',
                    props: true,
                },
            ],
        },
        {
            path: '/TextMessages',
            component: ViewsRoot,
            children: [
                { path: '', name: 'TextMessages', component: TextMessagesList },
                { path: 'new', name: 'NewTextMessage', component: NewTextMessage },
                { path: 'keywords', name: 'TextMessagesKeywords', component: TextMessagesKeywords },
                { path: 'responses', name: 'TextMessagesResponses', component: TextMessagesResponses },
                { path: 'responses/contact/:contactId', name: 'ResponseContact', component: Contact, props: true },
                { path: 'responses', name: 'TextMessagesResponses', component: TextMessagesResponses },
                { path: 'smsdialogs', name: 'SmsDialogs', component: SmsDialogs },
                { path: ':textMessageId/sending', name: 'TextMessageSending', component: TextMessageSending, props: true },
            ],
        },
        {
            path: '/Contact',
            component: ViewsRoot,
            children: [{ path: ':contactId', name: 'Contact', component: Contact, props: true }],
        },
        {
            path: '/Surveys',
            component: ViewsRoot,
            children: [
                { path: '', name: 'Surveys', component: SurveysList },
                { path: 'new', name: 'SurveysNew', component: SurveysNew },
                { path: ':surveyId/published', name: 'SurveyPublished', component: SurveyPublished, props: true },
            ],
        },
        {
            path: '/Events',
            component: ViewsRoot,
            children: [
                { path: '', name: 'Events', component: EventList },
                { path: 'new', name: 'EventsNew', component: EventsNew },
                { path: 'sponsors', name: 'EventSponsors', component: EventSponsors },
                { path: 'sponsors/new', name: 'EventSponsorNew', component: EventSponsor },
                { path: 'sponsors/:sponsorId', name: 'EventSponsor', component: EventSponsor, props: true },
                { path: 'speakers', name: 'EventSpeakers', component: Speakers },
                { path: 'speakers/new', name: 'EventSpeakerNew', component: Speaker },
                { path: 'speakers/:speakerId', name: 'EventSpeaker', component: Speaker, props: true },
            ],
        },
        {
            path: '/Segments',
            component: ViewsRoot,
            children: [
                {
                    path: '',
                    component: SegmentsRoot,
                    name: 'SegmentsRoot',
                    children: [
                        { path: '', name: 'Segments', component: SegmentsList },
                        {
                            path: ':segmentId',
                            name: 'SegmentEdit',
                            component: SegmentsEdit,
                            props: true,
                        },
                    ],
                },
            ],
        },
        {
            path: '/Journeys',
            component: ViewsRoot,
            children: [
                {
                    path: '',
                    component: JourneysRoot,
                    name: 'JourneysRoot',
                    children: [
                        { path: '', redirect: { name: 'JourneyHome', params: { status: 'drafts' } } },
                        { path: ':status(drafts|active|ended|templates)', component: JourneyHome, props: true, name: 'JourneyHome' },
                        { path: 'new', component: JourneyNew, props: true, name: 'JourneyNew' },
                        {
                            path: ':JourneyId([\\w\\-]{36})',
                            component: Viewport,
                            props: true,
                            name: 'JourneyViewport',
                        },
                    ],
                },
            ],
        },
        {
            path: '/BackOffice2',
            component: ViewsRoot,
            children: [
                {
                    path: '',
                    component: BackOfficeRoot,
                    name: 'BackOffice2',
                    children: [
                        { path: '', redirect: 'BackOffice2/Accounts' },
                        { path: 'SubscriptionStats', name: 'SubscriptionStats', component: ServiceSubscriptionStats },
                        { path: 'Accounts', name: 'AccountList', component: AccountList },
                        { path: 'Accounts/:accountId', name: 'AccountEdit', component: AccountEdit, props: true },
                        { path: 'Subscriptions', name: 'SubscriptionList', component: SubscriptionList },
                        { path: 'Subscriptions/:serviceSubscriptionId', name: 'SubscriptionEdit', component: SubscriptionEdit, props: true },
                        { path: 'Users', name: 'UserList', component: UserList },
                        { path: 'Users/:userId', name: 'UserEdit', component: UserEdit, props: true },
                        { path: 'Features', name: 'Features', component: Features },
                        { path: 'FeatureSets', name: 'FeatureSets', component: FeatureSets },
                        { path: 'Themes', name: 'Themes', component: Themes },
                        { path: 'ManageUsers', name: 'ManageUsers', component: ManageUsers },
                        { path: 'ManageWorkers', name: 'ManageWorkers', component: WorkerManagement },
                        { path: 'TaskManager', name: 'TaskManager', component: TaskManager },
                        { path: 'SmtpServers', name: 'SmtpServers', component: SmtpServers },
                        { path: 'SmtpServers/:smtpServerId', name: 'SmtpServerDetail', component: SmtpServerDetails, props: true },
                        { path: 'OAuthProviders', name: 'OAuthProviders', component: OAuthProviderList },
                        { path: 'OAuthProviders/New', name: 'NewOAuthProvider', component: EditOAuthProvider },
                        { path: 'OAuthProviders/:oauthProviderId', name: 'EditOAuthProvider', component: EditOAuthProvider, props: true },
                        { path: 'LatestIssues', name: 'LatestIssues', component: LatestIssues },
                    ],
                },
            ],
        },
    ];

    const router = createRouter({
        history: createWebHistory('/'),
        routes,
    });

    rootElement({
        name: 'vue-root', // pascal, camel & kebab-case are valid
        component: Root,
        vm: {},
        router,
        contextComponents: ['<div>waiting</div>'],
    });
};

initVueRoot();
